import React, { useEffect, useState } from 'react';
import { CalendarDays, MapPin, Check } from 'lucide-react';
import {
  useCalendarInvitesStore,
  respondToInvitation,
  ensureInvitationsLoaded,
} from '../services/calendarInvitesService';
import './Calendar.css';
import { isAppointmentShare } from '../utils/appointmentChatShare';
import { useAuthStore } from '../store/authStore';
import { usePreferencesStore } from '../store/preferencesStore';
import { useNotificationStore } from '../store/notificationStore';
import { formatTime, formatDayLong, fromDateKey } from '../utils/calendarDates';

/**
 * A shared appointment, rendered in the chat transcript (PLAN §3.4, beside
 * ShareMessageCard/TaskShareCard in the `share` kind's discriminator chain).
 *
 * Display fields ride in the metadata (Decision 5 — title/start/end/all_day/
 * location, never the description). The VIEWER'S OWN state comes from the
 * invites store (their pending/active rows by appointment_uid): an invitee
 * with a pending row gets Accept/Decline — through THE single service path,
 * like every other surface; the organizer sees their role; everyone else
 * sees the card without buttons (their invitation, if any, was created for
 * them personally — no token lives in this metadata for anyone to redeem).
 */
export default function AppointmentShareCard({ metadata }) {
  const meta = metadata || {};
  const [busy, setBusy] = useState(false);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const timeFormat = usePreferencesStore((s) => s.timeFormat);
  const pending = useCalendarInvitesStore((s) => s.pending);
  const active = useCalendarInvitesStore((s) => s.active);

  // First card of the session loads the viewer's invitation state (no-op
  // afterwards; realtime + rejoin keep it fresh).
  useEffect(() => {
    ensureInvitationsLoaded();
  }, []);

  if (!isAppointmentShare(meta)) return null;

  const myPending = pending.find((r) => r.appointment_uid === meta.appointment_uid);
  const myActive = active.find(
    (r) => r.appointment_uid === meta.appointment_uid && r.status === 'accepted'
  );
  const isOrganizer =
    currentUserId != null && String(currentUserId) === String(meta.organizer_id);

  const timeLabel = (() => {
    if (meta.all_day) {
      const start = fromDateKey(meta.start);
      return start ? `${formatDayLong(start)} · All day` : 'All day';
    }
    const start = meta.start ? new Date(meta.start) : null;
    const end = meta.end ? new Date(meta.end) : null;
    if (!start || Number.isNaN(start.getTime())) return '';
    return `${formatDayLong(start)} · ${formatTime(start, timeFormat)}${
      end && !Number.isNaN(end.getTime()) ? ` – ${formatTime(end, timeFormat)}` : ''
    }`;
  })();

  const respond = async (action) => {
    if (busy || !myPending?.token) return;
    setBusy(true);
    const { success, info, error } = useNotificationStore.getState();
    try {
      await respondToInvitation(myPending.token, action, myPending);
      if (action === 'accept') success('Added to your calendar');
      else info('Invitation declined');
    } catch (err) {
      error(err?.response?.data?.error || err.message || 'Failed to respond to invitation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="chat-share-card" data-testid="appointment-share-card">
      <div className="chat-share-card-head">
        <CalendarDays size={14} className="chat-share-card-typeicon" />
        <span style={{ fontWeight: 600, fontSize: 13 }}>{meta.title || '(no title)'}</span>
      </div>
      {timeLabel && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{timeLabel}</div>
      )}
      {meta.location && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
          <MapPin size={11} />
          {meta.location}
        </div>
      )}

      {isOrganizer ? (
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>You're the organizer</div>
      ) : myActive ? (
        <div className="flex items-center gap-1.5" style={{ marginTop: 6 }}>
          <Check size={12} style={{ color: '#16a34a' }} />
          <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 500 }}>In your calendar</span>
        </div>
      ) : myPending ? (
        <div className="flex items-center gap-2" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="cal-btn"
            disabled={busy}
            onClick={() => respond('decline')}
          >
            Decline
          </button>
          <button
            type="button"
            className="cal-btn cal-btn--primary"
            disabled={busy}
            onClick={() => respond('accept')}
          >
            Accept
          </button>
        </div>
      ) : null}
    </div>
  );
}
